/**
 * How complete the profile is — A2's `completeness` (profile-store.ts:207)
 * over real answers instead of the mock's.
 *
 * The answers counted are the 16 `ONBOARDING_STEPS` (the server says which
 * are `completed`, `GET /me/onboarding`), plus one more the intake never
 * asks: a body-fat NUMBER younger than {@link BODY_FAT_FRESH_DAYS} — typed by
 * hand, or estimated from a body photo. A2 counted a stale one as missing
 * ("A última medida tem mais de um mês"); a person who never measured gets the
 * same row, with its own sentence.
 *
 * ── A PHOTO ESTIMATE COUNTS LIKE A TYPED NUMBER (`D05`) ────────────────────
 * Most people have no access to a caliper or a DEXA, and the photo estimate is
 * good enough to follow the trend — so once either source has answered, the
 * app stops asking. It may still OFFER a better measurement; it never nags.
 * That is why {@link bodyFatState} reads the NEWER of the two dates.
 *
 * Pure: `now` is a parameter, so the tests pin it.
 */
import type { MeasurementListOutput } from "@repo/contract/training";
import { ONBOARDING_STEPS, type OnboardingStep } from "@repo/contract/training";
import type { StaticEditorId } from "../editors";
import type { SectionId } from "./section-meta";

export const BODY_FAT_FRESH_DAYS = 31;

const DAY_MS = 86_400_000;

/** Where a missing answer is fixed: the editor, and the section it lives in. */
export interface AnswerTarget {
  readonly edit: StaticEditorId;
  readonly section: SectionId;
}

/**
 * The section an editor lives in, from the targets below: where the "Perfil"
 * tab opens a page so the editor it asked for has its context under it
 * (`D218`). `null` for an editor no onboarding step names.
 */
export function sectionOfEditor(id: string): SectionId | null {
  for (const target of Object.values(STEP_TARGETS)) {
    if (target.edit === id) {
      return target.section;
    }
  }
  return null;
}

/**
 * Each onboarding step's editor. The three routine steps share one editor,
 * and so do the session length and its hard limit.
 */
export const STEP_TARGETS: Readonly<Record<OnboardingStep, AnswerTarget>> = {
  date_of_birth: { edit: "birth", section: "you" },
  sex: { edit: "sex", section: "you" },
  height: { edit: "height", section: "you" },
  weight: { edit: "weight", section: "body" },
  goal: { edit: "goal", section: "goal" },
  secondary_goal: { edit: "secondary", section: "goal" },
  days_per_week: { edit: "days", section: "routine" },
  days_fixed: { edit: "same", section: "routine" },
  fixed_weekdays: { edit: "days", section: "routine" },
  session_minutes: { edit: "sessionMinutes", section: "routine" },
  session_hard_limit: { edit: "sessionMinutes", section: "routine" },
  parq: { edit: "parq", section: "health" },
  current_pain: { edit: "pain-new", section: "pain" },
  equipment_place: { edit: "place", section: "equipment" },
  equipment: { edit: "equipment", section: "equipment" },
  experience: { edit: "experience", section: "goal" },
};

export type BodyFatState = "fresh" | "none" | "stale";

/**
 * Whether a body-fat number — typed, or estimated from a photo — is recent
 * enough to count. The NEWER of the two dates decides: `fresh` within
 * {@link BODY_FAT_FRESH_DAYS}, `stale` when the newer one is older than that,
 * `none` when neither source has ever answered.
 *
 * `photoLatestAt` is a plain optional parameter with a default so that every
 * caller that has no photo half yet keeps compiling unchanged; the body-photos
 * phase (`T109`) passes `bodyAssessmentsOptions`' latest `done` date in. A
 * date that does not parse is treated as no answer rather than as a fresh one.
 */
export function bodyFatState(
  series: Pick<MeasurementListOutput, "latest"> | null,
  now: Date,
  photoLatestAt: string | null = null
): BodyFatState {
  const times = [series?.latest?.measuredAt ?? null, photoLatestAt]
    .filter((at): at is string => at !== null)
    .map((at) => Date.parse(at))
    .filter((time) => Number.isFinite(time));
  if (times.length === 0) {
    return "none";
  }
  const age = now.getTime() - Math.max(...times);
  return age > BODY_FAT_FRESH_DAYS * DAY_MS ? "stale" : "fresh";
}

export type MissingAnswer =
  | (AnswerTarget & { readonly kind: "step"; readonly step: OnboardingStep })
  | (AnswerTarget & {
      readonly kind: "body-fat";
      readonly state: Exclude<BodyFatState, "fresh">;
    });

export interface Completeness {
  readonly answered: number;
  /** Deduplicated by editor: one row per thing to answer. */
  readonly missing: readonly MissingAnswer[];
  readonly pct: number;
  readonly total: number;
}

export function completeness(input: {
  readonly bodyFat: BodyFatState;
  readonly completed: readonly OnboardingStep[];
}): Completeness {
  const done = new Set(input.completed);
  const missing: MissingAnswer[] = [];
  const seen = new Set<string>();
  let answered = 0;
  for (const step of ONBOARDING_STEPS) {
    if (done.has(step)) {
      answered += 1;
      continue;
    }
    const target = STEP_TARGETS[step];
    if (!seen.has(target.edit)) {
      seen.add(target.edit);
      missing.push({ ...target, kind: "step", step });
    }
  }
  if (input.bodyFat === "fresh") {
    answered += 1;
  } else {
    missing.push({
      edit: "bodyFat",
      kind: "body-fat",
      section: "body",
      state: input.bodyFat,
    });
  }
  const total = ONBOARDING_STEPS.length + 1;
  return {
    answered,
    missing,
    pct: Math.round((answered / total) * 100),
    total,
  };
}

/** How many missing answers a section holds — the chip dot and its badge. */
export function missingIn(
  result: Completeness | null,
  section: SectionId
): number {
  return result === null
    ? 0
    : result.missing.filter((m) => m.section === section).length;
}
